import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CourseService from "../../../Api/CourseService";
import VouchersService from "../../../Api/VouchersService";
import PaymentService from "../../../Api/PaymentService";
import ClientFooter from "../ClientFooter"
import { AuthContext } from "../../../Context/AuthContext";
import '../../../styles/common.css';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const CourseDetailesView = () => {
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [voucherCode, setVoucherCode] = useState("");
    const [voucherError, setVoucherError] = useState("");
    const [voucherApplied, setVoucherApplied] = useState(false);
    const [discountedPrice, setDiscountedPrice] = useState(null);
    const [voucherDetails, setVoucherDetails] = useState(null);
    const [showFullCaution, setShowFullCaution] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const courseService = new CourseService();
    const vouchersService = new VouchersService();
    const paymentService = new PaymentService();
    const { cookies } = useContext(AuthContext);

    useEffect(() => {
        
        const params = new URLSearchParams(location.search);
        const courseId = params.get('id');
        if (courseId) {
            fetchCourseDetails(courseId);
        }

        // Load Razorpay script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [location]);

    const fetchCourseDetails = async (courseId) => {
        try {
            const response = await courseService.GetforsaleById(courseId);
            if (response?.data?.status === "success") {
                setCourse(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching course details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleVoucherSubmit = async (e) => {
        e.preventDefault();
        setVoucherError("");

        if (!voucherCode.trim()) {
            setVoucherError("Please enter a voucher code");
            return;
        }

        try {
            const response = await vouchersService.validateVoucher({
                code: voucherCode,
                product_type: "course",
                product_id: course.id
            });

            if (response.data.status === "success" && response.data.data.valid) {
                setVoucherApplied(true);
                setVoucherDetails(response.data.data.voucher);

                // Calculate discounted price
                const originalPrice = parseFloat(course.price);
                let newPrice;

                if (response.data.data.voucher.voucher_type === "percentage") {
                    const discount = originalPrice * (parseFloat(response.data.data.voucher.discount_value) / 100);
                    newPrice = originalPrice - discount;
                } else {
                    newPrice = originalPrice - parseFloat(response.data.data.voucher.discount_value);
                }

                setDiscountedPrice(newPrice.toFixed(2));
                setVoucherError("");
            } else {
                setVoucherError("Invalid voucher code");
                setVoucherApplied(false);
                setVoucherDetails(null);
                setDiscountedPrice(null);
            }
        } catch (error) {
            setVoucherError("Error validating voucher");
            setVoucherApplied(false);
            setVoucherDetails(null);
            setDiscountedPrice(null);
        }
    };

    const handlePayment = async () => {
        try {
            // Create order
            const orderData = {
                course_id: course.id,
                voucher_code: voucherCode || null
            };

            const orderResponse = await paymentService.AddPayment(orderData);
            
            if (orderResponse.data.status === "success") {
                // Get the Razorpay key from the response
                const razorpayKey = orderResponse.data.data.key_id;
                
                const options = {
                    key: razorpayKey,
                    amount: orderResponse.data.data.amount,
                    currency: "INR",
                    name: "Day Trading",
                    description: `Payment for ${course.title}`,
                    order_id: orderResponse.data.data.order_id,
                    handler: async function (response) {
                        try {
                            // Verify payment
                            const verifyData = {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature
                            };

                            const verifyResponse = await paymentService.VerifyPayment(verifyData);
                            if (verifyResponse.data.status === "success") {
                                // Payment successful
                                Swal.fire({
                                    title: 'Success!',
                                    text: 'Payment successful!',
                                    icon: 'success',
                                    confirmButtonText: 'Continue',
                                    confirmButtonColor: '#28a745'
                                }).then(() => {
                                    navigate('/StudentDashboard');
                                });
                            } else {
                                Swal.fire({
                                    title: 'Payment Failed',
                                    text: 'Payment verification failed!',
                                    icon: 'error',
                                    confirmButtonText: 'OK',
                                    confirmButtonColor: '#dc3545'
                                });
                            }
                        } catch (error) {
                            console.error("Payment verification error:", error);
                            Swal.fire({
                                title: 'Payment Failed',
                                text: 'Payment verification failed!',
                                icon: 'error',
                                confirmButtonText: 'OK',
                                confirmButtonColor: '#dc3545'
                            });
                        }
                    },
                    prefill: {
                        name: cookies.user?.name || "User",
                        email: cookies.user?.email || "",
                        contact: cookies.user?.phone || ""
                    },
                    theme: {
                        color: "#3399cc"
                    }
                };

                const razorpay = new window.Razorpay(options);
                razorpay.open();
            }
        } catch (error) {
            console.error("Payment error:", error);
            Swal.fire({
                title: 'Payment Failed',
                text: 'Payment initiation failed!',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#dc3545'
            });
        }
    };

    const toggleCaution = () => {
        setShowFullCaution(!showFullCaution);
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!course) {
        return <div>Course not found</div>;
    }

    return (
        <>
            <div className="rbt-breadcrumb-default rbt-breadcrumb-style-3">
                <div className="breadcrumb-inner">
                    <img src="assets1/images/bg/bg-image-10.jpg" alt="Education Images" />
                </div>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="content">
                                <div className="content text-start">
                                    <ul className="page-list">
                                        <li className="rbt-breadcrumb-item">
                                            <a href="index.html">Home</a>
                                        </li>
                                        <li>
                                            <div className="icon-right">
                                                <i className="feather-chevron-right" />
                                            </div>
                                        </li>
                                        <li className="rbt-breadcrumb-item active">{course.title}</li>
                                    </ul>
                                    <h2 className="title">
                                        {course.title}
                                    </h2>
                                    <p className="description">
                                        {course.description}
                                    </p>

                                    <div className="d-flex align-items-center mb--20 flex-wrap rbt-course-details-feature">
                                        <div className="feature-sin total-student">
                                            <span>{course.current_students}/{course.max_students} students</span>
                                        </div>
                                    </div>

                                    <ul className="rbt-meta">
                                        <li>
                                            <i className="feather-calendar" />
                                            Validity: {course.validity_days} days
                                        </li>
                                        <li>
                                            <i className="feather-award" />
                                            {course.is_sold_out ? 'Sold Out' : 'Available'}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* End Breadcrumb Area */}
            <div className="rbt-course-details-area ptb--60">
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-8">
                            <div className="course-details-content">
                                <div className="rbt-course-feature-box rbt-border-with-box thuumbnail">
                                    <img
                                        className="w-100"
                                        src={course.thumbnail_url}
                                        alt="Card image"
                                    />
                                </div>
                                <div className="rbt-inner-onepage-navigation sticky-top mt--30">
                                    <nav className="mainmenu-nav onepagenav">
                                        <ul className="mainmenu">
                                            <li className="current">
                                                <a href="#overview">Overview</a>
                                            </li>
                                            <li>
                                                <a href="#coursecontent">Course Content</a>
                                            </li>
                                            <li>
                                                <a href="#details">Details</a>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                                {/* Start Course Feature Box  */}
                                <div
                                    className="rbt-course-feature-box overview-wrapper rbt-border-with-box mt--30 has-show-more"
                                    id="overview"
                                >
                                    <div className="rbt-course-feature-inner has-show-more-inner-content">
                                        <div className="section-title">
                                            <h4 className="rbt-title-style-3">What you'll learn</h4>
                                        </div>
                                        <div className={`caution-content ${!showFullCaution ? 'caution-collapsed' : ''}`}>
                                            {course.caution}
                                        </div>
                                        {course.caution && course.caution.split('\n').length > 6 && (
                                            <div className="rbt-show-more-btn" onClick={toggleCaution}>
                                                {showFullCaution ? 'Show Less' : 'Show More'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* End Course Feature Box  */}
                                {/* Start Course Content  */}
                                <div
                                    className="course-content rbt-border-with-box coursecontent-wrapper mt--30"
                                    id="coursecontent"
                                >
                                    <div className="rbt-course-feature-inner">
                                        <div className="section-title">
                                            <h4 className="rbt-title-style-3">Course Content</h4>
                                        </div>
                                        <div className="rbt-accordion-style rbt-accordion-02 accordion">
                                            <div className="accordion" id="accordionExampleb2">
                                                {course.categories.map((category, index) => (
                                                    <div className="accordion-item card" key={category.id}>
                                                        <h2 className="accordion-header card-header" id={`heading${category.id}`}>
                                                            <button
                                                                className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                                                                type="button"
                                                                data-bs-toggle="collapse"
                                                                data-bs-target={`#collapse${category.id}`}
                                                                aria-expanded={index === 0}
                                                                aria-controls={`collapse${category.id}`}
                                                            >
                                                                {category.name}
                                                                <span className="rbt-badge-5 ml--10">{category.videos.length} videos</span>
                                                            </button>
                                                        </h2>
                                                        <div
                                                            id={`collapse${category.id}`}
                                                            className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                                                            aria-labelledby={`heading${category.id}`}
                                                        >
                                                            <div className="accordion-body card-body pr--0">
                                                                <ul className="rbt-course-main-content liststyle">
                                                                    {category.videos.map((video) => (
                                                                        <li key={video.id}>
                                                                            <a href={video.vimeo_url}>
                                                                                <div className="course-content-left">
                                                                                    <i className="feather-play-circle" />
                                                                                    <span className="text">{video.title}</span>
                                                                                </div>
                                                                                <div className="course-content-right">
                                                                                    <span className="min-lable">
                                                                                        {video.video_duration || 'N/A'}
                                                                                    </span>
                                                                                </div>
                                                                            </a>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>


                            </div>

                        </div>
                        <div className="col-lg-4">
                            <div className="course-sidebar sticky-top rbt-border-with-box course-sidebar-top rbt-gradient-border">
                                <div className="inner">
                                    {/* Start Viedo Wrapper  */}
                                    <a
                                        className="video-popup-wrapper text-center popup-video sidebar-video-hidden mb--15"
                                        href="https://www.youtube.com/watch?v=IrMNqSMwtlM"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <div className="video-content">
                                            <img
                                                className="w-100 rbt-radius"
                                                src="https://img.youtube.com/vi/IrMNqSMwtlM/sddefault.jpg"
                                                alt="Video Images"
                                            />
                                            <div className="position-to-top">
                                                <span className="rbt-btn rounded-player-2 with-animation">
                                                    <span className="play-icon" />
                                                </span>
                                            </div>
                                            <span className="play-view-text d-block color-white">
                                                <i className="feather-eye" /> Preview this course
                                            </span>
                                        </div>
                                    </a>
                                    {/* End Viedo Wrapper  */}
                                    <div className="content-item-content">
                                        <div className="rbt-price-wrapper d-flex flex-wrap align-items-center justify-content-between">
                                            <div className="rbt-price">
                                                {discountedPrice ? (
                                                    <>
                                                        <span className="current-price">₹{discountedPrice}</span>
                                                        <span className="off-price">₹{course.price}</span>
                                                    </>
                                                ) : (
                                                    <span className="current-price">₹{course.price}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="voucher-section mt--15">
                                            <form onSubmit={handleVoucherSubmit} className="d-flex gap-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter voucher code"
                                                    value={voucherCode}
                                                    onChange={(e) => setVoucherCode(e.target.value)}
                                                />
                                                <button
                                                    type="submit"
                                                    className="rbt-btn btn-gradient btn-sm"
                                                >
                                                    Apply
                                                </button>
                                            </form>
                                            {voucherError && (
                                                <div className="text-danger mt-2">{voucherError}</div>
                                            )}
                                            {voucherApplied && voucherDetails && (
                                                <div className="text-success mt-2">
                                                    Voucher applied! {voucherDetails.voucher_type === "percentage" ?
                                                        `${voucherDetails.discount_value}% off` :
                                                        `₹${voucherDetails.discount_value} off`}
                                                </div>
                                            )}
                                        </div>

                                        <div className="add-to-cart-button mt--15">
                                            {
                                                course.purchased ? (
                                                    <a
                                                        className="rbt-btn btn-gradient icon-hover w-100 d-block text-center"
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            navigate('/StudentDashboard'); // Navigate to dashboard if purchased
                                                        }}
                                                    >
                                                        <span className="btn-text">View Course</span>
                                                        <span className="btn-icon">
                                                            <i className="feather-arrow-right" />
                                                        </span>
                                                    </a>
                                                ) : (
                                                    <a
                                                        className="rbt-btn btn-gradient icon-hover w-100 d-block text-center"
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (cookies.token) {
                                                                handlePayment();
                                                            } else {
                                                                navigate('/StudentLogin');
                                                            }
                                                        }}
                                                    >
                                                        <span className="btn-text">{cookies.token ? 'Buy Now' : 'Login to Buy'}</span>
                                                        <span className="btn-icon">
                                                            <i className="feather-arrow-right" />
                                                        </span>
                                                    </a>
                                                )
                                            }
                                        </div>

                                        <div className="rbt-widget-details mt--20">
                                            <ul className="rbt-course-details-list-wrapper">
                                                <li>
                                                    <span>Duration</span>
                                                    <span className="rbt-feature-value rbt-badge-5">
                                                        {course.validity_days} Days
                                                    </span>
                                                </li>
                                                <li>
                                                    <span>Total Students</span>
                                                    <span className="rbt-feature-value rbt-badge-5">
                                                        {course.current_students}/{course.max_students}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span>Total Categories</span>
                                                    <span className="rbt-feature-value rbt-badge-5">
                                                        {course.categories.length}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span>Total Videos</span>
                                                    <span className="rbt-feature-value rbt-badge-5">
                                                        {course.categories.reduce((total, category) => total + category.videos.length, 0)}
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        
            {/* Start Footer aera */}

            {/* End Footer aera */}
            <div className="rbt-progress-parent">
                <svg
                    className="rbt-back-circle svg-inner"
                    width="100%"
                    height="100%"
                    viewBox="-1 -1 102 102"
                >
                    <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
                </svg>
            </div>

            <ClientFooter />
        </>
    );
};

export default CourseDetailesView;
